import React, { FC, useRef } from 'react';
import { Control, useController } from 'react-hook-form';
import { SafeCapType } from 'shared';

import { ChevronDownIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOptionsPortalWrapper,
  FormCombobox,
  FormComboboxOption,
} from '../../../../../../components/combobox/index';
import { safeCapTypeOptions, safeCapTypeTitle } from '../../../../../../types/safes.types';
import { getTranslation } from '../../../../../../utils/get-translation.util';
import { FundraisingFormSchema } from '../../../fundraising-validation';

const capTypeOptions = safeCapTypeOptions;

/**
 * Headless UI writes the selected value straight into the input when it syncs, so without an
 * explicit `displayValue` the field shows the raw enum (`pre-money`) instead of its label.
 */
const capTypeLabel = (capType?: null | string) =>
  capType && capType in safeCapTypeTitle
    ? getTranslation(safeCapTypeTitle[capType as SafeCapType])
    : '';

export type CapTypeComboboxProps = {
  control: Control<FundraisingFormSchema>;
  index: number;
};

/**
 * Cap type picker for a scenario's custom SAFE row. Same shape as the stakeholder / share class
 * pickers in this table: a read-only combobox input whose options are portalled out of the
 * horizontally scrolling table so the list is never clipped by it.
 *
 * Always selectable, like every other cell in the row — the fields can be filled in any order, and
 * a cap type picked before its cap is simply not sent until the cap exists.
 */
export const CapTypeCombobox: FC<CapTypeComboboxProps> = ({ control, index }) => {
  const inputRef = useRef<HTMLDivElement>(null);
  const name = `customSafes.safeItems.${index}.capType` as const;
  const { field, fieldState } = useController({ control, name });

  // The form validates on every keystroke, so "cap type is required" exists from the moment a cap
  // is typed — several fields before the user reaches this one. Hold the message back until they
  // have actually been to the control, otherwise a half-filled row turns red mid-entry.
  const errorMessage =
    fieldState.isTouched && fieldState.error
      ? (fieldState.error.message ?? getTranslation('CAP_TYPE_REQUIRED'))
      : false;

  return (
    <FormCombobox className="relative w-full" control={control} name={name}>
      <div className="relative w-full" ref={inputRef}>
        <ComboboxInput
          autoComplete="off"
          displayValue={capTypeLabel}
          errorMessage={errorMessage}
          icon={<ChevronDownIcon className="mt-2" />}
          onBlur={field.onBlur}
          onChange={() => undefined}
          readOnly
          value={capTypeLabel(field.value)}
          wrapperClassName="h-10"
        />
        <ComboboxButton className="absolute left-0 top-0 z-20 h-full w-full" />
      </div>
      <ComboboxOptionsPortalWrapper anchorRef={inputRef} itemsCount={capTypeOptions.length}>
        <ComboboxOptions className="z-20 h-fit max-h-[150px] w-full overflow-x-hidden">
          {capTypeOptions.map((capType) => (
            <FormComboboxOption control={control} key={capType} name={name} value={capType}>
              <span className="text-sm text-gray-700">
                <AppFormattedMessage id={safeCapTypeTitle[capType]} />
              </span>
            </FormComboboxOption>
          ))}
        </ComboboxOptions>
      </ComboboxOptionsPortalWrapper>
    </FormCombobox>
  );
};
